import React, { useContext } from 'react';
import { Upload, Typography, Spin, Grid, Button } from 'antd';
import { FileAddOutlined, CloseOutlined, FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FileAttached from './FileAttached';
import toast from 'react-hot-toast';
import uploadDocuments from '../../utils/session/uploadDocuments';
import discardDocuments from '../../utils/session/discardDocuments';
import startSession from '../../utils/session/startSession';
import RecordingContext from './RecordingContext';

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_FILES = 3;

// Define allowed MIME types and their corresponding extensions
const ALLOWED_TYPES_MAP = {
  'application/pdf': ['pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx']
};

// Derive arrays from the map to avoid duplication
const ALLOWED_TYPES = Object.keys(ALLOWED_TYPES_MAP);
const ALLOWED_EXTENSIONS = [...new Set(Object.values(ALLOWED_TYPES_MAP).flat())];

export default function FileUploadSection({ onChange, attachedFiles = [], selectedPatient = null }) {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const ctx = useContext(RecordingContext);

  // Ensure attachedFiles is always an array
  const files = attachedFiles || [];

  const isDisabled = isProcessing || files.length >= MAX_FILES;

  const handleUpload = async (file) => {
    if (!selectedPatient) {
      toast(t('session_files.errors.select_patient_first'), {
        icon: <InfoCircleOutlined />,
    });
      return false;
    }
    
    if (files.length >= MAX_FILES) {
      toast.error(t('session_files.errors.max_files_reached'));
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('session_files.errors.file_too_large'));
      return false;
    }
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    // Check if file type is allowed
    const isTypeAllowed = file.type ? ALLOWED_TYPES.includes(file.type) : true;
    const isExtensionAllowed = ALLOWED_EXTENSIONS.includes(fileExtension);
    
    // For mobile camera files, sometimes the MIME type might be empty or generic
    // So we rely more on file extension for image files
    // Allow any image/* MIME type or files with image extensions
    const isGenericImageAllowed = file.type && file.type.startsWith('image/');
    const isImageExtension = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'jfif', 'jif', 'tiff', 'tif'].includes(fileExtension);
    
    if (!isTypeAllowed && !isGenericImageAllowed && !isImageExtension) {
      toast.error(t('session_files.errors.unsupported_format'));
      return false;
    }
    const isDuplicate = files.some(attachedFile => 
      attachedFile.name === file.name && attachedFile.size === file.size
    );
    if (isDuplicate) {
      toast.error(t('session_files.errors.duplicate_file'));
      return false;
    }
    setIsProcessing(true);
    try {
      let currentSessionUuid = ctx.sessionUuid;
      
      // If no session exists, create one
      if (!currentSessionUuid) {
        currentSessionUuid = await startSession({ 
          precharting: true,
          patientUuid: selectedPatient?.getUUID()
        });
        if (!currentSessionUuid) {
          throw new Error('Failed to create session');
        }
        // Set the session UUID in the context
        ctx.setSessionUuid(currentSessionUuid);
      }
      
      await uploadDocuments(currentSessionUuid, [file]);
      const newFiles = [...files, file];
      onChange?.(newFiles);
    } catch (err) {
      console.error('File upload error:', err);
      console.error('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
      toast.error(t('session_files.errors.upload_failed'));
    } finally {
      setIsProcessing(false);
    }
    return false;
  };

  const handleRemoveAll = async () => {
    setIsProcessing(true);
    try {
      if (ctx.sessionUuid) {
        await discardDocuments(ctx.sessionUuid);
      }
      onChange?.([]);
      // Clear the session UUID if we're removing all documents
      ctx.setSessionUuid(null);
    } catch (err) {
      toast.error(t('session_files.errors.discard_failed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Renders different content based on upload state: loading, files attached, or default upload hint
  const renderUploadContent = () => {
    // Show loading state
    if (isProcessing) {
      return (
        <div style={{ marginTop: 15 }}>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <Spin size="large" />
          </div>
          <Paragraph style={{ marginTop: 10, marginBottom: 0, textAlign: 'center' }}>
            {t('common.processing')}
          </Paragraph>
        </div>
      );
    }

    // Show attached files
    if (files.length > 0) {
      const fileNames = files.map(file => file.name).join(', ');
      const fileCount = files.length;
      return (
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {files.length === 1 ? (
              <FileAttached 
                fileName={files[0].name}
                onRemove={handleRemoveAll}
              />
            ) : (
              <>
                <FileTextOutlined style={{ fontSize: 40, color: '#1890ff' }} />
                <Button 
                  type="text" 
                  icon={<CloseOutlined style={{ fontSize: 12, color: 'gray' }} />}
                  style={{ 
                    color: 'gray', 
                    padding: 0,
                    position: 'absolute',
                    top: -15,
                    right: -12,
                    background: 'white',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    width: 22,
                    height: 22,
                    minWidth: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAll();
                  }}
                />
              </>
            )}
          </div>
          {files.length > 1 && (
            <Paragraph style={{ marginTop: 10, marginBottom: 5, color: '#666' }}>
              {t('session_files.files_attached', {
                count: fileCount,
                plural: fileCount > 1 ? 's' : '',
                names: fileNames
              })}
            </Paragraph>
          )}
        </div>
      );
    }

    // Show default upload hint
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <FileAddOutlined style={{ fontSize: 25, color: '#1890ff', marginRight: 12 }} />
          <Paragraph style={{ margin: 0, textAlign: 'left', color: '#888', fontSize: 15 }}>
            {screens.sm ? t('session_files.upload_hint') : t('session_files.upload_hint_mobile')}
          </Paragraph>
        </div>
      </>
    );
  };

  // Main component render - shows file upload section with title and dragger
  return (
    <div style={{ marginTop: 35, textAlign: 'left', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <FileAddOutlined style={{ marginRight: 8 }} />
        <Paragraph style={{ margin: 0 }}>{t('session_files.title')}</Paragraph>
      </div>
      
      <Dragger
        accept="*"
        beforeUpload={handleUpload}
        showUploadList={false}
        multiple={false}
        disabled={isDisabled}
        style={{ 
          cursor: isDisabled ? 'default' : 'pointer',
          marginTop: 10,
          opacity: isDisabled ? 0.6 : 1,
          padding: '0px'
        }}
        data-testid="document-upload-dragger"
      >
        {renderUploadContent()}
      </Dragger>
    </div>
  );
} 